import { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch } from 'react-icons/md';
import { describeScope } from 'components/apiKeyScope/ScopeBuilder';
import { useIndustries } from 'services/industryService';
import ScopeEditor from './ScopeEditor';

interface ApiKeyRow {
    _id: string;
    ownerId: string;
    ownerEmail: string;
    ownerName: string;
    prefix: string;
    scope: string[];
    status: 'active' | 'revoked';
    createdAt: string;
    lastUsedAt: string | null;
    pendingRequestCount: number;
}

interface ScopeRequestRow {
    apiKeyId: string;
    requestId: string;
    ownerId: string;
    ownerEmail: string;
    ownerName: string;
    currentScope: string[];
    requestedScope: string[];
    message: string;
    requestedAt: string;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function BackOfficeApiKeys() {
    const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [requests, setRequests] = useState<ScopeRequestRow[]>([]);
    const [requestsLoading, setRequestsLoading] = useState(true);
    const [confirm, setConfirm] = useState<{ req: ScopeRequestRow; action: 'accept' | 'reject' } | null>(null);
    const [rejectNote, setRejectNote] = useState('');
    const [busyId, setBusyId] = useState<string | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<ApiKeyRow | null>(null);

    const { industries } = useIndustries();

    const loadApiKeys = () => {
        setLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/admin/api-keys`, { ...authHeaders(), params: { search, limit: 100 } })
            .then((res) => {
                setApiKeys(res.data?.data ?? []);
                setTotal(res.data?.total ?? 0);
            })
            .catch(() => setError('Erreur lors du chargement des clés API.'))
            .finally(() => setLoading(false));
    };

    const loadRequests = () => {
        setRequestsLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/admin/api-keys/scope-requests`, authHeaders())
            .then((res) => setRequests(res.data?.data ?? []))
            .catch(() => setError('Erreur lors du chargement des demandes de scope.'))
            .finally(() => setRequestsLoading(false));
    };

    useEffect(loadRequests, []);
    useEffect(() => {
        const t = setTimeout(loadApiKeys, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const handleRequestAction = async (req: ScopeRequestRow, action: 'accept' | 'reject') => {
        setBusyId(req.requestId);
        setError(null);
        try {
            await axios.post(
                `${CONFIG.apiUrl}/admin/api-keys/${req.apiKeyId}/scope-requests/${req.requestId}/${action}`,
                action === 'reject' ? { note: rejectNote } : {},
                authHeaders()
            );
            setRequests((prev) => prev.filter((r) => r.requestId !== req.requestId));
            loadApiKeys();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors du traitement de la demande.');
        } finally {
            setBusyId(null);
            setConfirm(null);
            setRejectNote('');
        }
    };

    const handleToggleStatus = async (key: ApiKeyRow) => {
        const path = key.status === 'active' ? 'deactivate' : 'activate';
        setBusyId(key._id);
        setError(null);
        try {
            await axios.post(`${CONFIG.apiUrl}/admin/api-keys/${key._id}/${path}`, {}, authHeaders());
            loadApiKeys();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la mise à jour du statut.');
        } finally {
            setBusyId(null);
        }
    };

    const activeCount = apiKeys.filter((k) => k.status === 'active').length;

    return (
        <div className="pt-[50px] flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                {[
                    { label: 'Clés API', value: loading ? '—' : String(total) },
                    { label: 'Clés actives', value: loading ? '—' : String(activeCount) },
                    { label: 'Demandes de scope en attente', value: requestsLoading ? '—' : String(requests.length) },
                ].map((kpi) => (
                    <Card key={kpi.label} extra="p-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{kpi.label}</p>
                        <p className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">{kpi.value}</p>
                    </Card>
                ))}
            </div>

            {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

            <Card extra="p-5">
                <p className="mb-4 text-xl font-bold text-navy-700 dark:text-white">Demandes de scope en attente</p>

                {requestsLoading ? (
                    <div className="py-10 text-center text-gray-500">Chargement...</div>
                ) : requests.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">Aucune demande en attente.</div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {requests.map((req) => (
                            <div
                                key={req.requestId}
                                className="flex flex-col gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 dark:border-navy-600 dark:bg-navy-700"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <p className="font-semibold text-navy-700 dark:text-white">{req.ownerName || '—'}</p>
                                        <p className="text-xs text-gray-500">{req.ownerEmail}</p>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                            Demandé le {new Date(req.requestedAt).toLocaleDateString('fr-FR')}
                                        </p>
                                    </div>

                                    {confirm?.req.requestId === req.requestId ? (
                                        <div className="flex flex-col items-end gap-2">
                                            {confirm.action === 'reject' && (
                                                <input
                                                    type="text"
                                                    value={rejectNote}
                                                    onChange={(e) => setRejectNote(e.target.value)}
                                                    placeholder="Note (optionnelle)"
                                                    className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                                                />
                                            )}
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleRequestAction(req, confirm.action)}
                                                    disabled={busyId === req.requestId}
                                                    className={`rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 ${
                                                        confirm.action === 'accept'
                                                            ? 'bg-green-500 hover:bg-green-600'
                                                            : 'bg-red-500 hover:bg-red-600'
                                                    }`}
                                                >
                                                    {busyId === req.requestId ? '...' : 'Confirmer'}
                                                </button>
                                                <button
                                                    onClick={() => { setConfirm(null); setRejectNote(''); }}
                                                    className="rounded-md bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-300"
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setConfirm({ req, action: 'accept' })}
                                                className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 whitespace-nowrap"
                                            >
                                                Accepter
                                            </button>
                                            <button
                                                onClick={() => setConfirm({ req, action: 'reject' })}
                                                className="rounded-md bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-300"
                                            >
                                                Refuser
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <div>
                                        <p className="text-xs font-semibold uppercase text-gray-500">Scope actuel</p>
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {req.currentScope.length === 0 ? (
                                                <span className="text-xs text-gray-400">aucun</span>
                                            ) : (
                                                req.currentScope.map((s) => (
                                                    <span key={s} className="rounded-full bg-white px-2 py-0.5 text-xs text-navy-700">
                                                        {describeScope(s)}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold uppercase text-gray-500">Scope demandé</p>
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {req.requestedScope.map((s) => (
                                                <span key={s} className="rounded-full bg-brand-500 px-2 py-0.5 text-xs text-white">
                                                    {describeScope(s)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {req.message && <p className="text-xs text-gray-600">{req.message}</p>}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card extra="p-5">
                <p className="mb-4 text-xl font-bold text-navy-700 dark:text-white">Clés API</p>

                <div className="relative mb-4 max-w-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MdSearch className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Rechercher par nom, société, email ou téléphone de l’acheteur..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Chargement...</div>
                ) : apiKeys.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">Aucune clé API.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-navy-600">
                                    <th className="py-2 pr-3">Propriétaire</th>
                                    <th className="py-2 pr-3">Clé</th>
                                    <th className="py-2 pr-3">Statut</th>
                                    <th className="py-2 pr-3">Scope</th>
                                    <th className="py-2 pr-3">Dernière utilisation</th>
                                    <th className="py-2 pr-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiKeys.map((key) => (
                                    <tr key={key._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-2 pr-3">
                                            <p className="font-medium text-navy-700 dark:text-white">{key.ownerName || '—'}</p>
                                            <p className="text-xs text-gray-500">{key.ownerEmail}</p>
                                        </td>
                                        <td className="py-2 pr-3 font-mono text-xs">{key.prefix}...</td>
                                        <td className="py-2 pr-3">
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                                    key.status === 'active'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-200 text-gray-600'
                                                }`}
                                            >
                                                {key.status === 'active' ? 'Active' : 'Désactivée'}
                                            </span>
                                            {key.pendingRequestCount > 0 && (
                                                <span className="ml-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                                                    {key.pendingRequestCount} en attente
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3">
                                            <div className="flex max-w-xs flex-wrap gap-1">
                                                {key.scope.length === 0 ? (
                                                    <span className="text-xs text-gray-400">aucun</span>
                                                ) : (
                                                    key.scope.slice(0, 2).map((s) => (
                                                        <span key={s} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-navy-700">
                                                            {describeScope(s)}
                                                        </span>
                                                    ))
                                                )}
                                                {key.scope.length > 2 && (
                                                    <span className="text-xs text-gray-400">+{key.scope.length - 2}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2 pr-3 text-xs text-gray-500">
                                            {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString('fr-FR') : 'jamais'}
                                        </td>
                                        <td className="py-2 pr-3">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setEditing(key)}
                                                    className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-navy-700 hover:bg-gray-100 dark:text-white"
                                                >
                                                    Éditer scope
                                                </button>
                                                <button
                                                    onClick={() => handleToggleStatus(key)}
                                                    disabled={busyId === key._id}
                                                    className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-navy-700 hover:bg-gray-100 disabled:opacity-50 dark:text-white"
                                                >
                                                    {key.status === 'active' ? 'Désactiver' : 'Activer'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {editing && (
                <ScopeEditor
                    apiKeyId={editing._id}
                    initialScope={editing.scope}
                    industries={industries}
                    onClose={() => setEditing(null)}
                    onSaved={(scope) => {
                        setApiKeys((prev) => prev.map((k) => (k._id === editing._id ? { ...k, scope } : k)));
                    }}
                />
            )}
        </div>
    );
}
